import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { manpowerApi, type MpRequest, type MpRequestCreate } from '@/lib/apiClient';
// Direct files, not the `@/components` barrel: the barrel re-exports every
// component, so importing two of them through it drags the whole set (and
// their dependencies) into this module's graph — enough to time out vitest's
// module import in a fresh worker. Same reason the rules live in ./lib.
import Modal from '@/components/Modal';
import Button from '@/components/Button';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import {
  type RequestFormValues, initialFormValues, seedFromContext, isForecastRequest, formCopy,
  suggestedStageFor, validateRequestForm, toPayload, blockedMessage, selectedChange, changeLabel,
} from './lib/manpowerRequestForm';

/**
 * The Manpower Request form (#303 slice 2) — native to PMT, replacing the
 * Formstack Manpower Change Form deep link. Since slice 4 (#386) it is also
 * the estimator's SYSTAC forecast update: same form, the job decides which
 * face it wears (`ctx.requestType`), and every string that differs lives in
 * `formCopy`.
 *
 * The reason it exists here rather than in Formstack (product owner with
 * operations, 2026-08-31): it opens KNOWING the job. The job number, PM, whether the job
 * runs fab/field phases and whether crew is already booked all arrive from
 * `GET /manpower/requests/new-context` — the PM never types a job number, and
 * `stage` (the field the old form never had) arrives pre-answered. On a bid the
 * forecast the Tracker already holds arrives too, and the fields open filled
 * with it so the estimator states what CHANGED.
 *
 * ⚠️ Crew size is FREE TEXT and is sent exactly as typed. "2-3", "Whole crew"
 * and a 71-character breakdown are all real submissions. Nothing here parses
 * it, and nothing downstream applies it to the grid mechanically — a scheduler
 * reads it and books by hand, always (operations: "to ensure all context is
 * captured that was discussed during the approval process"). A forecast update
 * changes nothing in Bid Tracker either — it is the estimator telling the manpower lead.
 *
 * ⚠️ Who may submit is decided by the API (`context.canRequest`), not here.
 * This form disables itself on a `false`, but the 403 is the enforcement.
 */

// The form's rules — stage default, refusals, payload shape, who is told no,
// the copy — live in ./lib/manpowerRequestForm so they are tested without a DOM.

const FIELD: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const LABEL: React.CSSProperties = {
  font: 'var(--ew-fw-semibold) 11px/1.3 var(--ew-font)', letterSpacing: '.06em',
  textTransform: 'uppercase', color: 'var(--ew-n-500)',
};
const INPUT: React.CSSProperties = {
  font: 'var(--ew-fw-regular) 14px/1.4 var(--ew-font)', padding: '7px 10px',
  border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', background: 'var(--ew-white)',
  color: 'var(--ew-dark)',
};
const HINT: React.CSSProperties = { font: 'var(--ew-fw-regular) 12px/1.4 var(--ew-font)', color: 'var(--ew-n-400)' };

export default function RequestManpowerModal({
  projectId, isOpen, onClose,
}: {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const ctxQ = useQuery({
    queryKey: ['manpower', 'request-context', projectId],
    queryFn: () => manpowerApi.requestContext(projectId),
    enabled: isOpen && Number.isFinite(projectId),
    staleTime: 30_000,
  });
  const ctx = ctxQ.data;

  // ⚠️ EVERYTHING here starts fresh because the CARD MOUNTS THIS COMPONENT ONLY
  // WHILE IT IS OPEN — there is no "reset on reopen" anywhere, on purpose.
  //
  // Two review rounds on #439 found two different pieces of state surviving a
  // close: first the stage (an open-reset to EMPTY overwrote the value the
  // cached context had already supplied), then the mutation's last error (the
  // reset cleared the form but not `submit`, so a failed send greeted the next
  // fresh form). A mounted-but-closed modal has to remember to reset every such
  // piece by hand, and the list only grows. Mounting per open resets all of
  // them by construction: state, the mutation, the success panel.
  //
  // The initial stage — and on a bid, the forecast pre-fill — come from
  // whatever context is cached (normally it is — the card shares the query key
  // and fetched it before the click).
  const [values, setValues] = useState<RequestFormValues>(() => initialFormValues(ctx));
  const [touched, setTouched] = useState(false);
  // Has the user chosen a stage by hand? Once they have, no arriving context may
  // overwrite it — a refetch after staleTime would otherwise flip their answer.
  const [stageTouched, setStageTouched] = useState(false);
  const [created, setCreated] = useState<MpRequest | null>(null);

  // First visit to a job: the modal mounted BEFORE the context was cached, so
  // pre-answer `stage` when it arrives — unless the user has already chosen —
  // and seed the forecast into whichever fields are still blank (only those:
  // seedFromContext never overwrites a typed value).
  useEffect(() => {
    if (!ctx) return;
    setValues((v) => seedFromContext(stageTouched ? v : { ...v, stage: suggestedStageFor(ctx, v.changeId) }, ctx));
  }, [ctx, stageTouched]);

  const chooseStage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStageTouched(true);
    setValues((v) => ({ ...v, stage: e.target.value as RequestFormValues['stage'] }));
  };

  // Pointing the form at a change (or back at the job) re-answers the stage
  // from THAT target's own signal — unless the user has chosen one by hand.
  // Review of #442: a new PCO on a job with base crew inherited the job's
  // "change" and read as "a change to what was already proposed for PCO-003".
  const chooseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const changeId = e.target.value;
    setValues((v) => ({ ...v, changeId, stage: stageTouched ? v.stage : suggestedStageFor(ctx, changeId) }));
  };

  const submit = useMutation({
    mutationFn: (body: MpRequestCreate) => manpowerApi.createRequest(body),
    onSuccess: (r) => {
      setCreated(r);
      // The inbox, its status counts and the badge are stale the moment this lands.
      qc.invalidateQueries({ queryKey: ['manpower', 'requests'] });
      qc.invalidateQueries({ queryKey: ['manpower', 'request-open-count'] });
      // A first forecast update flips the bid's suggested stage to 'change'.
      qc.invalidateQueries({ queryKey: ['manpower', 'request-context', projectId] });
    },
  });

  const set = <K extends keyof RequestFormValues>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setValues((v) => ({ ...v, [k]: e.target.value as RequestFormValues[K] }));

  const forecast = isForecastRequest(ctx);
  const change = selectedChange(ctx, values.changeId);
  const copy = formCopy(ctx, values.changeId);
  const blocked = blockedMessage(ctx);
  const validation = touched ? validateRequestForm(values, ctx) : null;
  const serverError = submit.isError
    ? ((submit.error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'The request could not be sent. Try again.')
    : null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!ctx || blocked || validateRequestForm(values, ctx)) return;
    submit.mutate(toPayload(projectId, ctx, values));
  }

  const title = ctx
    ? <>{copy.title} — <span style={{ fontVariantNumeric: 'tabular-nums' }}>{ctx.jobNumber}</span>{ctx.title ? ` ${ctx.title}` : ''}</>
    : copy.title;

  // ── Sent ──────────────────────────────────────────────────────────────────
  if (created) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        footer={<Button variant="primary" onClick={onClose}>Done</Button>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, font: 'var(--ew-fw-regular) 14px/1.5 var(--ew-font)', color: 'var(--ew-dark)' }}>
          <div style={{ font: 'var(--ew-fw-bold) 16px/1.3 var(--ew-font)' }}>
            {copy.sentTitle(created.id)}
          </div>
          <div style={{ color: 'var(--ew-n-600)' }}>
            {copy.sentBody} You can follow it under{' '}
            <a href="/manpower/requests" style={{ color: 'var(--ew-green)' }}>Manpower ▸ Requests</a>.
          </div>
        </div>
      </Modal>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  const canSend = !!ctx && !blocked && !submit.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      closeOnBackdropClick={!submit.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submit.isPending}>Cancel</Button>
          <Button variant="primary" type="submit" form="mpr-form" disabled={!canSend}>
            {submit.isPending ? 'Sending…' : copy.button}
          </Button>
        </>
      }
    >
      {ctxQ.isPending && (
        <div style={{ ...HINT, padding: '8px 0' }}>Loading the job…</div>
      )}

      {ctxQ.isError && (
        <div style={{ color: 'var(--ew-danger, #b8321c)', fontSize: 13 }}>
          Couldn’t load this job.{' '}
          <Button variant="secondary" size="sm" onClick={() => ctxQ.refetch()}>Retry</Button>
        </div>
      )}

      {ctx && blocked && (
        <div
          role="status"
          style={{
            padding: '10px 12px', borderRadius: 'var(--ew-r-md)', fontSize: 13, lineHeight: 1.5,
            background: 'var(--ew-n-50)', border: '1px solid var(--ew-n-200)', color: 'var(--ew-n-600)',
          }}
        >
          {blocked}
        </div>
      )}

      {ctx && !blocked && (
        <form id="mpr-form" onSubmit={onSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={HINT}>
            For <strong style={{ color: 'var(--ew-dark)' }}>{ctx.jobNumber}</strong>
            {ctx.title ? ` — ${ctx.title}` : ''}
            {forecast
              ? <>{ctx.estimator?.name ? <> · Estimator {ctx.estimator.name}</> : null} · SYSTAC bid</>
              : <>
                  {ctx.pm?.name ? <> · PM {ctx.pm.name}</> : null}
                  {ctx.lifecycle === 'closed-won' && <> · won, awaiting a Sage job number</>}
                </>}
          </div>

          {/* What the crew is FOR (slice 5): the job, or one of its change
              orders. Offered only when the job has any. The choice decides the
              request's type — pco until the change is in Intacct, then co — and
              the copy below follows it. */}
          {!forecast && (ctx.changes?.length ?? 0) > 0 && (
            <div style={FIELD}>
              <label htmlFor="mpr-change" style={LABEL}>This crew is for</label>
              <select id="mpr-change" style={INPUT} value={values.changeId} onChange={chooseChange}>
                <option value="">The job itself</option>
                {ctx.changes.map((c) => (
                  <option key={c.id} value={String(c.id)}>{changeLabel(c)}</option>
                ))}
              </select>
              {change && !change.executed && (
                <div style={HINT}>
                  {change.changeNo} is not yet linked to an Intacct CO, so this is <strong>proposed</strong> manpower —
                  like a SYSTAC forecast. It becomes a CO request by itself when the change is linked.
                </div>
              )}
            </div>
          )}

          {/* Stage — the question the old form could not ask. Pre-answered. */}
          <fieldset style={{ ...FIELD, border: 0, padding: 0, margin: 0 }}>
            <legend style={LABEL}>{copy.stageLegend}</legend>
            <div style={{ display: 'flex', gap: 16, fontSize: 14, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="radio" name="mpr-stage" value="initial" checked={values.stage === 'initial'} onChange={chooseStage} />
                {copy.stageInitial}
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="radio" name="mpr-stage" value="change" checked={values.stage === 'change'} onChange={chooseStage} />
                {copy.stageChange}
              </label>
            </div>
            {suggestedStageFor(ctx, values.changeId) === 'change' && values.stage === 'initial' && (
              <div style={HINT}>{copy.stageHint}</div>
            )}
          </fieldset>

          {/* Crew — FREE TEXT, on purpose. */}
          <div style={FIELD}>
            <label htmlFor="mpr-crew" style={LABEL}>{copy.crewLabel}</label>
            <input
              id="mpr-crew" type="text" style={INPUT} maxLength={120} placeholder='e.g. 4, or "2-3", or "1 leader + 3"'
              value={values.crewSize} onChange={set('crewSize')} autoComplete="off"
            />
            <div style={HINT}>{copy.crewHint}</div>
          </div>

          {ctx.isFab && (
            <div style={FIELD}>
              <label htmlFor="mpr-phase" style={LABEL}>Which crew</label>
              <select id="mpr-phase" style={INPUT} value={values.phase} onChange={set('phase')}>
                <option value="">Either / not sure</option>
                <option value="fabrication">Shop — fabrication</option>
                <option value="installation">Field — installation</option>
              </select>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={FIELD}>
              <label htmlFor="mpr-start" style={LABEL}>{copy.startLabel}</label>
              <input id="mpr-start" type="date" style={INPUT} min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} value={values.startDate} onChange={set('startDate')} />
            </div>
            <div style={FIELD}>
              <label htmlFor="mpr-end" style={LABEL}>{copy.endLabel}</label>
              <input id="mpr-end" type="date" style={INPUT} min={values.startDate || DATE_INPUT_MIN} max={DATE_INPUT_MAX} value={values.endDate} onChange={set('endDate')} />
            </div>
          </div>
          {forecast && ctx.forecast?.durationDays != null && (
            <div style={{ ...HINT, marginTop: -8 }}>
              The bid anticipates {ctx.forecast.durationDays} days
              {ctx.forecast.startDate ? ` from ${ctx.forecast.startDate}` : ''}. Dates above are pre-filled from that.
            </div>
          )}

          <div style={FIELD}>
            <label htmlFor="mpr-reason" style={LABEL}>{copy.reasonLabel}</label>
            <textarea
              id="mpr-reason" rows={3} style={{ ...INPUT, resize: 'vertical' }} maxLength={4000}
              placeholder={copy.reasonPlaceholder}
              value={values.reason} onChange={set('reason')}
            />
          </div>

          <div style={FIELD}>
            <label htmlFor="mpr-staffing" style={LABEL}>{copy.staffingLabel} <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
            <textarea
              id="mpr-staffing" rows={2} style={{ ...INPUT, resize: 'vertical' }} maxLength={4000}
              placeholder={forecast ? 'Key trades, a particular leader, site constraints…' : 'Certifications, a particular leader, site requirements…'}
              value={values.staffingNotes} onChange={set('staffingNotes')}
            />
          </div>

          {(validation || serverError) && (
            <div role="alert" style={{ color: 'var(--ew-danger, #b8321c)', fontSize: 13 }}>
              {validation ?? serverError}
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
